import { AfterViewInit, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Signal, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import '@kin/web-components/dist/external/kin-dialog/kin-dialog';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoOptions } from '@maskito/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert, lucideCircleUserRound } from '@ng-icons/lucide';

import { FEATURE_FLAGS } from 'app/app-config/configurations/splitIO-config';
import { TextInputComponent } from 'app/home/common/components/forms/text-input/text-input.component';
import { SocialProofComponent } from 'app/home/common/components/social-proof/social-proof.component';
import { VALIDATION_CONSTANTS } from 'app/home/common/constants/validation-constants';
import { utilToIsoDate } from 'app/home/common/utils/iso-date-type/to-iso-date';
import { getStateFullName } from 'app/home/common/utils/state-mapping/state-mapping';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, LockedQuoteAlertComponent, WithLockableFields } from 'app/home/create-quote/form-locking';
import { AboutYouQuoteFormInput } from 'app/home/create-quote/forms/about-you/about-you.types';
import { ageValidator } from 'app/home/create-quote/forms/shared/age.validator';
import mask from 'app/home/create-quote/forms/shared/date-mask-options';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { BundlingDialogService, BundlingDialogVariant } from 'app/home/create-quote/services/bundling-dialog/bundling-dialog.service';
import { ElementVisibilityService } from 'app/home/create-quote/services/element-visibility/element-visibility.service';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { UserStore } from 'app/home/global-store/user/user.store';
import { PHONE_NUMBERS } from 'app/shared/constants/contact-constants';
import { PhonePipe } from 'app/shared/pipes/phone-number/phone.pipe';
import { GoogleTagManagerService, SegmentEvent } from 'app/shared/services/analytics/google-tagmanager/gtm.service';
import { SplitIOService } from 'app/shared/services/splitIO/splitIO.service';
import { getCookie } from 'app/shared/utils/cookies/cookie-helpers';

@Component({
  selector: 'app-about-you',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MaskitoDirective, FormCardComponent, ReactiveFormsModule, NgIconComponent, TextInputComponent, PhonePipe, LockedQuoteAlertComponent, SocialProofComponent],
  providers: [provideIcons({ lucideCircleUserRound, lucideCircleAlert })],
  templateUrl: './about-you.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/about-you',
})
export class QuoteFormAboutYouComponent extends BaseQuoteForm<AboutYouQuoteFormInput> implements AfterViewInit, Lockable {
  formDataIn!: AboutYouQuoteFormInput;
  gpcValue: boolean = false;
  private elementRef = inject(ElementRef);
  public elementVisibility = inject(ElementVisibilityService);
  private bundlingDialogService = inject(BundlingDialogService);
  private splitIOService = inject(SplitIOService);
  private userStore = inject(UserStore);

  private gtmService = inject(GoogleTagManagerService);
  private route = inject(ActivatedRoute);

  public csPhone = PHONE_NUMBERS.customerSupport;

  public hasTCPA = computed(() => this.elementVisibility.getVisibility()());

  readonly maskitoPhoneOptions: MaskitoOptions = {
    mask: ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  };

  readonly maskitoDateOptions: MaskitoOptions = mask;

  public formDefinition = {
    dob: new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(10), (control) => ageValidator(control, () => this.quoteDraftStore.address()?.state)],
      updateOn: 'blur',
    }),
    email: new FormControl<string>('', {
      validators: [Validators.required, Validators.email],
      updateOn: 'blur',
    }),
    firstName: new FormControl<string>('', {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    lastName: new FormControl<string>('', {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    phoneNumber: new FormControl<string>('', {
      validators: [Validators.required, Validators.pattern(VALIDATION_CONSTANTS.REGEX.phone)],
      updateOn: 'blur',
    }),
    hasOptedIntoSms: new FormControl<boolean>(true),
    optOutGpcTracking: new FormControl<boolean>(false),
    anonymousId: new FormControl<string>(''),
    hasGivenTcpaConsent: new FormControl<boolean>(true, {
      validators: [Validators.requiredTrue],
    }),
    hasGivenEigConsent: new FormControl<boolean>(true),
  };

  public aboutYouComponentForm = new FormGroup(this.formDefinition);

  public errorMessages = {
    firstName: {
      required: 'Your first name is required',
    },
    lastName: {
      required: 'Your last name is required',
    },
    dob: {
      required: 'Your date of birth is required.',
      minlength: 'Please enter a valid date in MM/DD/YYYY format.',
      invalidDateFormat: 'Please enter a valid date in MM/DD/YYYY format.',
      underage: 'Birthdate is outside the eligible range.',
      overMaxAge: 'Birthdate is outside the eligible range.',
    },
    phoneNumber: {
      required: 'Your phone number is required',
      pattern: 'Please enter a valid phone number',
    },
    email: {
      required: 'Your email address is required',
      pattern: 'Please enter a valid email address',
    },
    tcpaConsent: {
      requiredTrue: 'Please check the box to continue',
    },
    eigConsent: {
      requiredTrue: 'Please check the box to continue',
    },
  };

  public showTcpaError = signal(false);

  public isDialogOpen = false;

  public userState = computed(() => {
    const state = this.quoteDraftStore.address()?.state;
    return getStateFullName(state) ?? 'your area';
  });

  @ViewChild('tcpaVisibilityError') tcpaVisibilityError?: ElementRef;
  @ViewChild('tcpaConsentCheckbox') tcpaConsentCheckbox?: ElementRef;
  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  // Feature flag for EIG consent
  public showEigConsent = computed(() => {
    const treatment = this.splitIOService.getTreatment(FEATURE_FLAGS.QT_1034_TCPA_CONSENT_EIG);
    return treatment === 'show';
  });

  public showSocialProof = computed(() => {
    const treatment = this.splitIOService.getTreatment(FEATURE_FLAGS.AR_QUOTING_ABOUT_YOU_SOCIAL_PROOF);
    return treatment === 'Show';
  });

  /**
   * Determines which title should be shown based on the `entryType` and presence of pre-filled data.
   * If the user is a Click Lead and has provided any pre-filled PII, the confirmation title is shown.
   * Note that `hasOptedIntoSms` is excluded from the check since it is not possible to pre-fill it via Click Leads.
   * @returns {boolean} True if the confirmation title should be shown, false otherwise.
   * @description This is used to conditionally render the confirmation title in the template.
   */
  public shouldShowConfirmationTitle = computed(() => {
    const isClickLead = this.userStore.entryType() === 'click-lead';
    if (!isClickLead) return false;

    const formData = this.formDataIn;
    if (!formData) return false;

    const ignoredKeys = ['hasOptedIntoSms', 'optOutGpcTracking', 'anonymousId'];
    const keysToCheck = Object.keys(formData).filter((key) => !ignoredKeys.includes(key));

    return keysToCheck.some((key) => {
      const value = formData[key as keyof AboutYouQuoteFormInput];
      return !!value;
    });
  });

  public pageTitle = computed(() => {
    return this.shouldShowConfirmationTitle() ? 'Confirm your information to progress to a quote' : 'Enter your information to get a quote';
  });

  private changeRef = inject(ChangeDetectorRef);

  constructor() {
    super();
    effect((): void => {
      this.formDataIn = this.quoteDraftStore.aboutYouQuoteFormSelector();
      if (this.formDataIn) {
        this.aboutYouComponentForm.patchValue({
          dob: this.formDataIn.dob,
          email: this.formDataIn.email,
          firstName: this.formDataIn.firstName,
          lastName: this.formDataIn.lastName,
          phoneNumber: this.formDataIn.phoneNumber,
          hasOptedIntoSms: this.formDataIn.hasOptedIntoSms,
          optOutGpcTracking: this.formDataIn.optOutGpcTracking,
        });
      }

      const smsControl = this.aboutYouComponentForm.get('hasOptedIntoSms');
      if (smsControl) {
        if (this.isFieldLocked('hasOptedIntoSms')) {
          smsControl?.disable();
        } else {
          smsControl?.enable();
        }
      }

      // If all fields are locked, we can assume the user has already given consent
      if (this.allFieldsLocked()) {
        // TCPA consent is always required
        this.aboutYouComponentForm.controls.hasGivenTcpaConsent.setValue(true);
        this.aboutYouComponentForm.controls.hasGivenTcpaConsent.disable();
      }

      const eigControl = this.aboutYouComponentForm.get('hasGivenEigConsent');
      if (eigControl) {
        if (this.showEigConsent()) {
          eigControl.setValidators([Validators.requiredTrue]);
          if (this.allFieldsLocked()) {
            eigControl.setValue(true);
            eigControl.disable();
          }
        } else {
          eigControl.clearValidators();
          if (this.allFieldsLocked()) {
            eigControl.setValue(true);
          }
        }
        eigControl.updateValueAndValidity();
      }
    });

    // Scroll to the tcpa error when the error is shown
    effect(() => {
      if (this.showTcpaError()) {
        // Let Angular finish rendering the error element before trying to scroll to it
        this.changeRef.detectChanges();
        this.scrollToTcpaError();
      }
    });
  }

  public getFormControls() {
    // Manually filter hasGivenTcpaConsent, hasGivenEigConsent, optOutGpcTracking, and anonymousId from the form controls
    // because they are excluded from locking/submitted field tracking and related to consent and user tracking
    // Honestly, I just wanted to destructure but eslint chirps:
    // - First, about an unused variable so I rename it "_"
    // - Then it yells about  camelCase, PascalCase, UPPER_CASE  @typescript-eslint/naming-convention
    const ignoredKeys = new Set(['hasGivenTcpaConsent', 'hasGivenEigConsent', 'optOutGpcTracking', 'anonymousId']);

    return Object.fromEntries(Object.entries(this.aboutYouComponentForm.controls).filter(([key]) => !ignoredKeys.has(key)));
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.aboutYouComponentForm.markAllAsTouched();

    if (this.aboutYouComponentForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        // If the user hasn't viewed the consent details, show the error and scroll to it.
        // It is an explicit product requirement that the error persists, even after the user
        // has viewed the consent details. This is also why it is outside of the normal Angular
        // form validation
        if (!this.hasTCPA()) {
          this.showTcpaError.set(true);
          return;
        }
        const formValues = this.getFormValues();
        const onContinue = () => {
          super.saveFormData(formValues);
          this.gtmService.sendDataLayerEventWithQuoteData(SegmentEvent.ABOUT_YOU_FORM_SUBMITTED);
        };

        const variant = this.getBundlingDialogVariant();
        if (variant) {
          this.bundlingDialogService.openWithCallback(onContinue, variant);
        } else {
          onContinue();
        }
      }
    } else {
      this.scrollToAndFocusFirstInvalidFormControl();
    }
  }

  private getBundlingDialogVariant(): BundlingDialogVariant | null {
    const queryParams = this.route.snapshot.queryParams;
    const productInterest = (queryParams['product_interest'] ?? '').toLowerCase();
    const isHomeAuto = productInterest === 'home-auto' || productInterest === 'auto-home';
    if (!isHomeAuto) return null;

    if (this.userStore.entryType() === 'click-lead') {
      const treatment = this.splitIOService.getTreatment(FEATURE_FLAGS.AB_332);
      if (treatment === 'v3_postinfo' || treatment === 'v4_postonline') return treatment;
      return null;
    }
    const ab = queryParams['ab'];
    if (ab === '286-v3_postinfo') return 'v3_postinfo';
    if (ab === '286-v4_postonline') return 'v4_postonline';
    return null;
  }

  // Note: TCPA consent is not being saved in the store. Requiring TCPA consent via form validation and Fullstory tracking is sufficient for Legal requirements.
  private getFormValues(): Pick<QuoteDraftState, 'applicant' | 'hasOptedIntoSms' | 'productInterest' | 'pagePath'> {
    const { controls } = this.aboutYouComponentForm;
    const dob = controls.dob.value;
    const formattedDob = dob ? utilToIsoDate(dob) : undefined;
    const queryParams = this.route.snapshot.queryParams;
    const formValues = {
      applicant: {
        dob: formattedDob,
        email: controls.email.value ?? '',
        firstName: controls.firstName.value ?? '',
        lastName: controls.lastName.value ?? '',
        phoneNumber: controls.phoneNumber.value ?? '',
        languagePreference: navigator.language,
      },
      optOutGpcTracking: this.gpcValue,
      hasOptedIntoSms: this.aboutYouComponentForm.controls.hasOptedIntoSms.value ?? false,
      anonymousId: this.aboutYouComponentForm.controls.anonymousId.value ?? '',
      productInterest: queryParams['product_interest'] || undefined,
      pagePath: queryParams['page_path'] || undefined,
    };

    return formValues;
  }

  public scrollToAndFocusFirstInvalidFormControl() {
    const form = document.getElementById('quote-form');
    const invalidControls = form?.getElementsByClassName('ng-invalid');
    if (invalidControls && invalidControls[0]) {
      const firstInvalidControl = invalidControls[0] as HTMLElement;
      // Scroll to the first invalid control's label if it exists, otherwise scroll to the control itself.
      const scrollElement = firstInvalidControl.parentElement?.tagName.toLowerCase() === 'label' ? firstInvalidControl.parentElement : firstInvalidControl;
      scrollElement.scrollIntoView({ behavior: 'smooth' });
      firstInvalidControl.focus({ preventScroll: true });
    }
  }

  protected close() {
    this.isDialogOpen = false;
  }

  protected open() {
    this.isDialogOpen = true;
  }

  /**
   * We setup a resize and intersection observer after init because
   * we need to know the final rendered footer height because it is sticky
   * If the footer is no longer sticky, this can be rethought.
   */
  ngAfterViewInit(): void {
    // Depending on whether the EIG consent is shown determines which element we want to observe for the
    // visibility validation.
    // Since EIG comes after TCPA, we observe the EIG element to ensure both are visible.
    // If EIG is not present, we want to observe just the TCPA element instead.
    const consentElementId = this.showEigConsent() ? 'eig-consent-label' : 'tcpa-consent-label';
    const consentElement = this.elementRef.nativeElement.querySelector(`#${consentElementId}`) as HTMLLabelElement;

    // Set browser GPC value for use when saving the form data. This is to trigger GPC opt-out in our back-end systems.
    this.gpcValue = navigator.globalPrivacyControl ?? false;
    this.aboutYouComponentForm.controls.optOutGpcTracking.setValue(this.gpcValue);
    this.elementVisibility.observeElement(consentElement);

    // read the segment cookie from the browser storage
    const cookieId = getCookie('ajs_anonymous_id') || '';
    this.aboutYouComponentForm.controls.anonymousId.setValue(cookieId);
  }

  private scrollToTcpaError(): void {
    if (this.tcpaVisibilityError?.nativeElement) {
      this.tcpaVisibilityError.nativeElement.scrollIntoView({ behavior: 'smooth' });
      this.tcpaConsentCheckbox?.nativeElement?.focus();
    }
  }
}
